import {useCallback, useState} from 'react'
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {demoPeer, useDemoFamily} from '#/lib/demoFamily'
import {
  type FamilyLinkDirection,
  useConfirmFamilyLink,
  useResolveFamilyLinkRequest,
  useRevokeFamilyLink,
} from '#/state/queries/family'
import {useProfileQuery} from '#/state/queries/profile'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {
  PasswordConfirmDialog,
  usePasswordConfirmDialog,
} from './PasswordConfirmDialog'

function ProfileLine({did}: {did: string}) {
  const {data: profile, isLoading} = useProfileQuery({did})
  if (isLoading) return <Text style={a.text_md}>…</Text>
  return (
    <Text style={[a.text_md, a.font_bold]}>
      {profile?.displayName || profile?.handle || did.slice(0, 12)}
    </Text>
  )
}

/** Two-step accept: resolve the code, show who asked, then confirm. */
export function EnterCodePanel({
  onLinked,
}: {
  onLinked?: (created: {parentDid: string; childDid: string}) => void
}) {
  const {t} = useLingui()
  const theme = useTheme()
  const [codeInput, setCodeInput] = useState('')
  const [resolved, setResolved] = useState<{
    initiatorDid: string
    initiatorRole: FamilyLinkDirection
  } | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const resolve = useResolveFamilyLinkRequest()
  const confirm = useConfirmFamilyLink()

  const onResolve = useCallback(() => {
    const code = codeInput.trim()
    if (!code) return
    setErrorMsg(null)
    setResolved(null)
    resolve.mutate(code, {
      onSuccess: r => {
        if (r.alreadyUsed) {
          setErrorMsg(t`That code has already been used.`)
          return
        }
        setResolved({
          initiatorDid: r.initiatorDid,
          initiatorRole: r.initiatorRole,
        })
      },
      onError: () => setErrorMsg(t`That code is invalid or expired.`),
    })
  }, [codeInput, resolve, t])

  const onConfirm = useCallback(() => {
    const code = codeInput.trim()
    if (!code || !resolved) return
    confirm.mutate(code, {
      onSuccess: r => {
        setResolved(null)
        setCodeInput('')
        onLinked?.(r)
      },
      onError: e => {
        const msg =
          (e as {error?: string}).error === 'ParentLimitReached'
            ? t`This learner already has 2 linked parents.`
            : (e as {error?: string}).error === 'ChildLimitReached'
              ? t`You already have 5 linked children.`
              : t`Could not confirm the link.`
        setErrorMsg(msg)
      },
    })
  }, [codeInput, resolved, confirm, onLinked, t])

  const reset = useCallback(() => {
    setResolved(null)
    setErrorMsg(null)
  }, [])

  return (
    <View style={[a.gap_sm]}>
      <TextInput
        accessibilityLabel="Text input field"
        style={[
          styles.input,
          {color: theme.atoms.text.color},
          theme.atoms.border_contrast_low,
        ]}
        value={codeInput}
        onChangeText={setCodeInput}
        placeholder={t`Enter link code`}
        autoCapitalize="none"
        autoCorrect={false}
        editable={!resolved}
        returnKeyType="go"
        onSubmitEditing={onResolve}
        accessibilityHint={t`Looks up the code and shows who it came from`}
      />
      {resolved ? null : (
        <Button
          label={t`Check code`}
          size="small"
          color="secondary"
          onPress={onResolve}>
          <ButtonText>{t`Check code`}</ButtonText>
        </Button>
      )}
      {errorMsg ? <Text style={styles.error}>{errorMsg}</Text> : null}
      {resolved ? (
        <View style={[a.gap_sm, a.border, a.rounded_sm, a.p_sm]}>
          <Text style={a.text_sm}>
            {resolved.initiatorRole === 'child'
              ? t`A learner asked you to be their parent:`
              : t`A parent invited this learner:`}
          </Text>
          <ProfileLine did={resolved.initiatorDid} />
          <View style={[a.flex_row, a.gap_sm]}>
            <Button
              label={t`Confirm link`}
              size="small"
              color="primary"
              style={[a.flex_1]}
              onPress={onConfirm}>
              <ButtonText>{t`Confirm`}</ButtonText>
            </Button>
            <Button
              label={t`Cancel`}
              size="small"
              color="secondary"
              style={[a.flex_1]}
              onPress={reset}>
              <ButtonText>{t`Cancel`}</ButtonText>
            </Button>
          </View>
        </View>
      ) : null}
      {confirm.isPending && <ActivityIndicator />}
    </View>
  )
}

/**
 * Unlink is guarded twice on purpose: first an "Are you sure?" confirm (so a
 * stray tap can't break the link), then the account password (so only the
 * account owner can remove a family member). Both steps also apply in demo
 * mode.
 */
export function RemoveLinkButton({peerDid}: {peerDid: string}) {
  const {t: l} = useLingui()
  const theme = useTheme()
  const revoke = useRevokeFamilyLink()
  const {data: profile} = useProfileQuery({did: peerDid})
  const demo = useDemoFamily()
  const confirmControl = Dialog.useDialogControl()
  const passwordDialog = usePasswordConfirmDialog()

  // Demo peers have no real profile — use the simulated one.
  const demoPeerProfile =
    demo.enabled && demoPeer(demo.role).did === peerDid
      ? demoPeer(demo.role)
      : undefined
  const displayName =
    demoPeerProfile?.displayName ||
    profile?.displayName ||
    profile?.handle ||
    peerDid.slice(0, 14)

  const onConfirmRemove = useCallback(() => {
    passwordDialog.open(() =>
      revoke.mutate(peerDid, {
        onSuccess: () =>
          Toast.show(l`Removed from your family`, {type: 'success'}),
        onError: () =>
          Toast.show(l`Could not remove this link. Please try again.`, {
            type: 'error',
          }),
      }),
    )
  }, [passwordDialog, revoke, peerDid, l])

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={l`Unlink ${displayName}`}
        accessibilityHint={l`Asks you to confirm before removing the family link`}
        onPress={() => confirmControl.open()}
        disabled={revoke.isPending}
        style={({pressed}) => [pressed && {opacity: 0.6}]}>
        <Text style={styles.removeText}>{l`Unlink`}</Text>
      </Pressable>

      <Dialog.Outer control={confirmControl}>
        <Dialog.Handle />
        <Dialog.ScrollableInner label={l`Remove family link`}>
          <View style={[a.gap_lg]}>
            <View style={[a.gap_xs]}>
              <Text style={[a.text_2xl, a.font_bold]}>
                <Trans>Remove {displayName}?</Trans>
              </Text>
              <Text style={[a.text_md, theme.atoms.text_contrast_medium]}>
                <Trans>
                  You'll stop being linked as family on Taderok. You can invite
                  them again anytime.
                </Trans>
              </Text>
            </View>
            <View style={[a.flex_row, a.gap_sm, a.justify_end]}>
              <Button
                label={l`Cancel`}
                size="small"
                color="secondary"
                onPress={() => confirmControl.close()}>
                <ButtonText>{l`Cancel`}</ButtonText>
              </Button>
              <Button
                label={l`Yes, unlink`}
                size="small"
                color="negative"
                onPress={() => confirmControl.close(() => onConfirmRemove())}>
                <ButtonText>{l`Yes, unlink`}</ButtonText>
              </Button>
            </View>
          </View>
          <Dialog.Close />
        </Dialog.ScrollableInner>
      </Dialog.Outer>

      <PasswordConfirmDialog
        control={passwordDialog.control}
        description={l`Enter your password to remove ${displayName} from your family.`}
      />
    </>
  )
}

const styles = StyleSheet.create({
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
  },
  error: {
    color: '#ef4444',
    fontSize: 13,
  },
  removeText: {
    color: '#ef4444',
    fontSize: 13,
    fontWeight: '600',
  },
})
